import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import "../../Hero.css";

const CARDS = [
  { icon: 'calculate', title: 'TikTok & Social', desc: 'Earnings & Coins Analysis', grad: 'from-blue-500 via-cyan-500 to-teal-500' },
  { icon: 'savings', title: 'SIP & Investment', desc: 'Returns & Wealth Growth', grad: 'from-violet-500 via-purple-500 to-fuchsia-500' },
  { icon: 'trending_up', title: 'Loan Estimator', desc: 'Best Market Deals (UK/UAE)', grad: 'from-rose-500 via-pink-500 to-orange-500' },
];

const FIELDS = [
  { label: 'Loan Amount', value: '₹50,00,000', icon: 'payments', grad: 'from-blue-500 to-cyan-500' },
  { label: 'Interest Rate', value: '8.5% p.a.', icon: 'percent', grad: 'from-purple-500 to-pink-500' },
  { label: 'Tenure', value: '20 Years', icon: 'schedule', grad: 'from-indigo-500 to-blue-500' },
];

const Hero: React.FC = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [calcCount, setCalcCount] = useState(10);
  const [activeCard, setActiveCard] = useState(0);
  const [homeSearch, setHomeSearch] = useState('');

  // Dynamic import to avoid pulling calculatorRegistry (11 KB) into main.js
  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | null = null;
    import('../../stores/calculatorStore').then(({ calculatorStore }) => {
      if (!mounted) return;
      const state = calculatorStore.getState();
      if (state.calcs.length) setCalcCount(state.calcs.length);
      unsubscribe = calculatorStore.subscribe(s => {
        if (mounted) setCalcCount(s.calcs.length || 10);
      });
      if (!state.initialized) calculatorStore.fetch();
    }).catch(() => {});
    return () => {
      mounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (homeSearch.trim()) {
      navigate(`/calculators?q=${encodeURIComponent(homeSearch.trim())}`);
    }
  };

  useEffect(() => {
    const t = setInterval(() => setActiveCard(p => (p + 1) % CARDS.length), 3500);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" className="relative w-full overflow-x-hidden pt-28 sm:pt-32 lg:pt-36 pb-8 sm:pb-12" style={{ minHeight: '600px' }}>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Badge */}
        {/* <div className="flex justify-center mb-6">
          <div className={`inline-flex items-center gap-1.5 glass border px-3 sm:px-4 py-1.5 sm:py-2 rounded-full cursor-default max-w-[90vw] ${isDark ? 'border-indigo-500/30 text-indigo-300' : 'border-indigo-200 text-indigo-600'
            }`}>
            <span className="material-symbols-outlined text-[13px] sm:text-[14px] flex-shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wide sm:tracking-wider whitespace-nowrap">
              <span className="sm:hidden">AI Financial Estimator</span>
              <span className="hidden sm:inline">AI-Powered Financial & Social Estimator</span>
            </span>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDark ? 'bg-indigo-400' : 'bg-indigo-500'}`} />
          </div>
        </div> */}

        {/* Main grid */}
        <div className="grid lg:grid-cols-2 gap-4 lg:gap-6 items-start">

          {/* ── LEFT: Text ── */}
          <div className="text-center lg:text-left space-y-2 sm:space-y-3">
            <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-4xl font-black leading-[1.1] tracking-tight">
              <span className={isDark ? 'text-indigo-400' : 'text-indigo-600'}>Smarter Decisions</span>
              <span className={`block mt-0.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>Better Estimates</span>
            </h1>

            <p className={`text-xs sm:text-sm max-w-xl mx-auto lg:mx-0 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              AI-powered TikTok, SIP &amp; Mortgage Estimator. Instant results for USA, UK (NatWest/RBS), UAE (HSBC), India &amp; Canada.
            </p>

            {/* Search Bar */}
            <div className="max-w-md mx-auto lg:mx-0 pt-2 pb-1">
              <form 
                action="/calculators" 
                method="GET" 
                onSubmit={handleSearchSubmit} 
                role="search"
                className={`relative flex items-center rounded-2xl border p-1 ${
                  isDark ? 'bg-slate-900/60 border-white/10 focus-within:border-indigo-500/50' : 'bg-white border-slate-200 shadow-lg shadow-slate-200/40 focus-within:border-indigo-300'
                }`}
                style={{ transition: 'transform 0.3s, opacity 0.3s' }}
              >
                <span className="material-symbols-outlined absolute left-4 text-slate-400 text-[20px]">search</span>
                <input
                  type="search"
                  name="q"
                  id="search"
                  placeholder="Search 39+ financial calculators..."
                  value={homeSearch}
                  onChange={(e) => setHomeSearch(e.target.value)}
                  className="w-full pl-11 pr-24 py-2.5 bg-transparent border-0 focus:ring-0 focus:outline-none focus-visible:ring-0 text-xs sm:text-sm font-semibold text-slate-900 dark:text-white placeholder-slate-400"
                  aria-label="Search calculators"
                />
                <button
                  type="submit"
                  className="absolute right-2 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold active:scale-95 shadow-md shadow-indigo-500/20"
                  style={{ transition: 'transform 0.3s, opacity 0.3s' }}
                >
                  Search
                </button>
              </form>
            </div>

            {/* CTAs */}
            <div className="flex flex-col xs:flex-row sm:flex-row gap-2.5 sm:gap-3 justify-center lg:justify-start">
              <button onClick={() => navigate('/dashboard')}
                aria-label="Start calculating for free"
                className="btn-brand group px-5 sm:px-7 py-3.5 sm:py-4 rounded-xl text-sm sm:text-base font-bold flex items-center justify-center gap-2 relative overflow-hidden active:scale-95"
                style={{
                  transition: 'transform 0.3s, opacity 0.3s',
                  boxShadow: isDark
                    ? '0 12px 30px rgba(99,102,241,0.4), inset 0 0 0 1px rgba(255,255,255,0.2)'
                    : '0 12px 30px rgba(79,70,229,0.25), inset 0 0 0 1px rgba(255,255,255,0.2)'
                }}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none" />
                <span className="material-symbols-outlined text-[17px] sm:text-[18px] group-hover:rotate-12 transition-transform relative z-10">calculate</span>
                <span className="relative z-10">Start Calculating Free</span>
              </button>
              <button onClick={() => navigate('/dashboard')}
                aria-label="Explore all financial tools"
                className="btn-ghost group px-5 sm:px-7 py-3.5 sm:py-4 rounded-xl text-sm sm:text-base font-semibold flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[17px] sm:text-[18px] group-hover:scale-110 transition-transform">explore</span>
                Explore All Tools
              </button>
            </div>

            {/* Trust row */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-5 pt-1">
              <div className={`flex items-center gap-1.5 text-[11px] sm:text-sm cursor-default whitespace-nowrap ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <span className="material-symbols-outlined text-[14px] sm:text-[16px]" style={{ color: '#818cf8', fontVariationSettings: "'FILL' 1" }}>calculate</span>
                <span>{calcCount}+ Calculators</span>
              </div>
              <div className={`flex items-center gap-1.5 text-[11px] sm:text-sm cursor-default whitespace-nowrap ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <span className="material-symbols-outlined text-[14px] sm:text-[16px]" style={{ color: '#60a5fa', fontVariationSettings: "'FILL' 1" }}>security</span>
                <span>100% Secure</span>
              </div>
            </div>

            {/* Mobile mini cards */}
            <div className="lg:hidden grid grid-cols-3 gap-2 sm:gap-3 mt-0">
              {CARDS.map((c, i) => (
                <div key={i} onClick={() => navigate('/dashboard')}
                  className={`glass rounded-xl sm:rounded-2xl p-2.5 sm:p-4 cursor-pointer active:scale-95 ${activeCard === i ? 'border-indigo-500/40 shadow-lg shadow-indigo-500/20' : 'border-white/10'}`}
                  style={{ transition: 'transform 0.3s, opacity 0.3s' }}>
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${c.grad} rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-1.5 sm:mb-2 shadow-md ${activeCard === i ? 'scale-110' : ''}`}
                    style={{ transition: 'transform 0.3s' }}>
                    <span className="material-symbols-outlined text-white text-[14px] sm:text-[18px]">{c.icon}</span>
                  </div>
                  <p className={`text-[9px] sm:text-xs font-bold text-center leading-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>{c.title}</p>
                  <p className={`text-[8px] sm:text-[10px] text-center mt-0.5 hidden xs:block ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{c.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Calculator card ── */}
          <div className="relative hidden lg:block">

            <div className={`relative glass-strong rounded-[2rem] p-6 md:p-7 border hover:scale-[1.012] group overflow-hidden shadow-2xl ${isDark ? 'border-white/20 hover:border-indigo-500/50 shadow-indigo-500/10' : 'border-slate-200/60 hover:border-indigo-200 shadow-slate-300/40'
              }`}
            style={{ transition: 'transform 0.5s, opacity 0.5s' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className={`relative w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden ${isDark ? 'bg-gradient-to-br from-indigo-500 to-blue-600' : 'bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500'
                    }`}
                    style={{ transition: 'transform 0.3s, opacity 0.3s' }}>
                    <span className="material-symbols-outlined text-white text-[22px] relative z-10" style={{ fontVariationSettings: "'FILL' 1, 'wght' 600" }}>calculate</span>
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  </div>
                  <div>
                    <h2 className={`font-black text-base mb-0.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>Loan Calculator</h2>
                    <p className={`text-[10px] flex items-center gap-1.5 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                      Real-time AI analysis
                    </p>
                  </div>
                </div>
              </div>

              {/* Fields — one row */}
              <div className="grid grid-cols-3 gap-2 mb-5">
                {FIELDS.map((f, i) => (
                  <div key={i} className={`group/row relative flex flex-col items-center gap-1 p-2.5 rounded-xl border overflow-hidden cursor-default ${isDark ? 'border-white/10 hover:border-indigo-500/30 bg-slate-800/80' : 'border-slate-200/50 hover:border-indigo-200 bg-slate-50/50'
                    }`}
                    style={{ transition: 'transform 0.3s, opacity 0.3s' }}>
                    <div className="flex items-center gap-1.5 relative z-10">
                      <div className={`w-6 h-6 bg-gradient-to-br ${f.grad} rounded-md flex items-center justify-center`}>
                        <span className="material-symbols-outlined text-white text-[12px]">{f.icon}</span>
                      </div>
                      <span className={`text-[10px] font-semibold transition-colors ${isDark ? 'text-white/75' : 'text-slate-600'}`}>{f.label}</span>
                    </div>
                    <span className={`font-black text-sm relative z-10 ${isDark ? 'text-white' : 'text-slate-900'}`}>{f.value}</span>
                  </div>
                ))}
              </div>

              {/* Result */}
              <div className="relative overflow-hidden rounded-xl mb-2">
                <div className={`relative m-[1px] rounded-[calc(0.75rem-1px)] p-4 text-center border transition-colors ${isDark
                  ? 'bg-slate-900 border-white/5 shadow-inner'
                  : 'bg-indigo-50/50 border-indigo-100/50 shadow-sm'
                  }`} style={{ backdropFilter: 'blur(4px)' }}>
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <span className={`material-symbols-outlined text-[14px] ${isDark ? 'text-blue-300' : 'text-indigo-500'}`}>trending_up</span>
                    <p className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-blue-200' : 'text-indigo-600'}`}>Monthly EMI</p>
                  </div>
                  <p className="text-3xl font-black text-gradient mb-1">₹43,391</p>
                  <p className={`text-[10px] font-medium ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Total Interest: ₹54,13,840</p>
                </div>
              </div>

              {/* CTA */}
              <button onClick={() => navigate('/dashboard')}
                className="btn-brand w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 group/btn">
                <span className="group-hover/btn:scale-105 transition-transform">Calculate Now</span>
                <span className="material-symbols-outlined text-[16px] group-hover/btn:translate-x-1.5 transition-transform">arrow_forward</span>
              </button>
            </div>

          </div>
        </div>

        {/* LCP heading + paragraph (moved from TrustedBy to render immediately) */}
        <div className={`text-center mt-8 sm:mt-10 lg:mt-16 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
          <div className={`inline-flex items-center gap-2 border px-4 py-1.5 rounded-full mb-4 text-xs font-bold uppercase tracking-wide ${isDark ? 'border-indigo-500/25 text-indigo-300' : 'border-indigo-200 text-indigo-600'}`}
            style={{ background: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.06)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
            Why FinCalc?
          </div>
          <h2 className={`text-3xl sm:text-4xl lg:text-4xl font-black tracking-tight leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Built for <span className="text-gradient"> Accuracy &amp; Trust</span>
          </h2>
          <p className={`mt-2 text-sm sm:text-base max-w-xl mx-auto leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Free financial tools you can rely on — no ads, no gimmicks, just accurate maths.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
